import React from 'react';
import { type BalanceSheet } from '../../services/api';

interface BalanceSheetReportProps {
  data?: BalanceSheet;
  isLoading: boolean;
  asOfDate: string;
}

const BalanceSheetReport: React.FC<BalanceSheetReportProps> = ({ data, isLoading, asOfDate }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected date</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Balance Sheet</h3>
          <p className="text-sm text-gray-500">As of {new Date(asOfDate).toLocaleDateString()}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          data.isBalanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {data.isBalanced ? 'Balanced' : 'Out of Balance'}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Assets */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Assets</h4>
          
          {/* Current Assets */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Current Assets</h5>
            <div className="space-y-1">
              {data.assets.current.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <span className="text-sm">{item.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                  </div>
                  <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center font-medium pt-1 border-t">
                <span>Total Current Assets</span>
                <span>{formatCurrency(data.assets.current.total)}</span>
              </div>
            </div>
          </div>

          {/* Non-Current Assets */}
          {data.assets.nonCurrent.items.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Non-Current Assets</h5>
              <div className="space-y-1">
                {data.assets.nonCurrent.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <span className="text-sm">{item.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                    </div>
                    <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center font-medium pt-1 border-t">
                  <span>Total Non-Current Assets</span>
                  <span>{formatCurrency(data.assets.nonCurrent.total)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center font-bold text-lg pt-3 border-t">
            <span>Total Assets</span>
            <span>{formatCurrency(data.totalAssets)}</span>
          </div>
        </div>

        {/* Liabilities & Equity */}
        <div className="space-y-4">
          {/* Liabilities */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Liabilities</h4>
            
            {/* Current Liabilities */}
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Current Liabilities</h5>
              <div className="space-y-1">
                {data.liabilities.current.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <span className="text-sm">{item.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                    </div>
                    <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center font-medium pt-1 border-t">
                  <span>Total Current Liabilities</span>
                  <span>{formatCurrency(data.liabilities.current.total)}</span>
                </div>
              </div>
            </div>

            {/* Non-Current Liabilities */}
            {data.liabilities.nonCurrent.items.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Non-Current Liabilities</h5>
                <div className="space-y-1">
                  {data.liabilities.nonCurrent.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <span className="text-sm">{item.name}</span>
                        <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center font-medium pt-1 border-t">
                    <span>Total Non-Current Liabilities</span>
                    <span>{formatCurrency(data.liabilities.nonCurrent.total)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center font-bold pt-3 border-t">
              <span>Total Liabilities</span>
              <span>{formatCurrency(data.liabilities.total)}</span>
            </div>
          </div>

          {/* Equity */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Equity</h4>
            <div className="space-y-1">
              {data.equity.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <span className="text-sm">{item.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{item.code}</span>
                  </div>
                  <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center font-bold pt-1 border-t">
                <span>Total Equity</span>
                <span>{formatCurrency(data.equity.total)}</span>
              </div>
            </div>
          </div>

          {/* Total Liabilities & Equity */}
          <div className="bg-gray-100 p-4 rounded-lg">
            <div className="flex justify-between items-center font-bold text-lg">
              <span>Total Liabilities & Equity</span>
              <span>{formatCurrency(data.totalLiabilitiesAndEquity)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetReport;
